import { useTranslation } from "react-i18next";
import * as Yup from 'yup';
import { selectUser } from "../../utils/redux/userSlice";
import { useSelector } from "react-redux";
import { Modal, Spinner } from "react-bootstrap";
import { useEffect, useRef, useState } from "react";
import { ErrorMessage, Field, FieldProps, Form, Formik, useFormikContext } from "formik";
import './ModalAlarm.scss'
import { Machine } from "../../utils/models/Machine";
import { ISelectOptions } from "aw-combobox";
import { getMachinesByInstallation } from "../../utils/services/machine.service";
import { orderByLabel } from "aw-generic-functions";
import { Rule, SeverityType } from "../../pages/AlarmModulePage";
import { AWSelect } from "aw-select";
import { useTheme } from "../../utils/contexts/ThemeContext";


const ModalAlarm = (
    props: {
        rule?: Rule | null,
        showModal: boolean,
        installations: ISelectOptions[],
        onHide: (e: any) => void
        onSave: (alarm: Rule) => void
    }
) => {

    const initialValues: Rule = {
        active: true,
        name: '',
        description: '',
        param: '',
        min: null,
        max: null,
        severity: SeverityType.Critical,
        installation: null,
        machines: [],
        emails: []
    };

    const user = useSelector(selectUser)
    const [loading, setLoading] = useState(false)
    const { t } = useTranslation();
    const [selectedInstallation, setSelectedInstallation] = useState<ISelectOptions>();
    const [allMachinesObj, setAllMachinesObj] = useState<ISelectOptions[]>([])
    const [selectedMachines, setSelectedMachines] = useState<ISelectOptions[]>([])
    const [initialRule, setInitialRule] = useState<Rule | null>(initialValues);
    const ALL_MACHINES_OPTION: ISelectOptions = { value: -1, label: t('AllMachinesSelect') };
    let firstTimeSetMachines = useRef(true);

    useEffect(() => {
        setInitialRule(props.rule ?? initialValues);
        setSelectedMachines([]); // <--- resetea al abrir
        firstTimeSetMachines.current = true;

        if (props.rule?.installation) {
            const selectedInst = props.installations.find(
                (inst: ISelectOptions) => inst.value === props.rule?.installation
            );
            setSelectedInstallation(selectedInst);
        } else {
            setSelectedInstallation(undefined);
        }
    }, [props.rule, props.showModal]);

    const validationSchema = Yup.object({
        name: Yup.string().required(t('required')),
        param: Yup.string().required(t('required')),
        severity: Yup.string().required(t('required')),
        installation: Yup.string().required(t('required')),
        min: Yup.number().nullable(),
        max: Yup.number()
            .nullable()
            .test('at-least-one', t('minOrMaxRequired'), function (value) {
                const { min } = this.parent;
                if ((min === undefined || min === null) && (value === undefined || value === null)) {
                    return false; // Ambos están vacíos -> ERROR
                }
                return true; // Al menos uno está lleno
            })
            .test('is-greater', t('maxGreaterThanMin'), function (value) {
                const { min } = this.parent;
                if (min !== undefined && min !== null && value !== undefined && value !== null) {
                    return value >= min;
                }
                return true; // No validamos si falta alguno
            }),

    });

    useEffect(() => {
        setSelectedMachines([])
        if (selectedInstallation && selectedInstallation.value !== -1) {
            getMachinesByInstallation(user.token, Number(selectedInstallation.value))
                .then((m: Machine[]) => {
                    if (m.length === 0) throw new Error('No Machines');

                    const mObj = orderByLabel(m.map((m: Machine) => ({
                        label: m.name,
                        value: m.idmachine
                    })));

                    const updatedMachines: ISelectOptions[] = [ALL_MACHINES_OPTION, ...mObj];
                    setAllMachinesObj(updatedMachines);

                    if (props.rule?.machines && firstTimeSetMachines.current) {
                        const selected = updatedMachines.filter((m) =>
                            props.rule!.machines?.includes(Number(m.value))
                        );
                        setSelectedMachines(selected);
                        firstTimeSetMachines.current = false;
                    }


                })
                .catch((e) => console.error("Error getMachinesByInstallation:", e))
                .finally(() => setTimeout(() => setLoading(false), 250));
        }
    }, [selectedInstallation]);

    const handleHide = (e: any) => {
        setInitialRule(initialValues);
        props.onHide(e);
    }

    const handleSave = (rule: Rule) => {
        if (!rule.id) rule.id = Date.now().toString(),
            setInitialRule(initialValues);
        rule.machines = selectedMachines.map(m => Number(m.value))
        props.onSave(rule);
    }

    const handleMachinesSelected = (selectedOptions: ISelectOptions[]) => {
        const hasAll = selectedOptions.some((opt) => opt.value === -1);
        if (hasAll) {
            setSelectedMachines([selectedOptions.find((opt) => opt.value === -1)!])
        } else {
            setSelectedMachines(selectedOptions)
        }
    };

    return (
        <>
            <Modal dialogClassName="aw-modal-alarm" show={props.showModal} keyboard={false} backdrop={'static'} size="lg">
                <Modal.Header className="position-relative d-block">
                    <p className="modal-title-custom m-0 text-start text-lg-center">{t('NewRule')}</p>
                    <button type='button' className='btn-close custom-close position-absolute end-0 top-50 translate-middle-y me-3' aria-label='Close' onClick={props.onHide} style={{ color: 'var(--text-primary)' }}> </button>
                </Modal.Header>
                <Modal.Body>
                    <Formik initialValues={initialRule!} validationSchema={validationSchema} onSubmit={(rule: Rule) => { handleSave(rule) }} enableReinitialize>
                        <InnerForm
                            loading={loading}
                            installations={props.installations}
                            selectedInstallation={selectedInstallation}
                            machines={allMachinesObj}
                            selectedMachines={selectedMachines}
                            onHide={handleHide}
                            onSelectedInstallation={setSelectedInstallation}
                            onSelectedMachines={handleMachinesSelected}
                        />
                    </Formik>
                </Modal.Body>
                <Modal.Footer>
                    <button form="alarmForm" className="btn-Custom btn-IDGreen" type="submit"><i className="bi bi-save"></i></button>
                </Modal.Footer>
            </Modal>
        </>
    )
}

const InnerForm = (props:
    {
        loading: boolean,
        installations: ISelectOptions[],
        selectedInstallation: ISelectOptions | undefined,
        machines: ISelectOptions[],
        selectedMachines: ISelectOptions[] | undefined,
        onHide: (e: any) => void,
        onSelectedInstallation: (opt: ISelectOptions) => void
        onSelectedMachines: (opts: ISelectOptions[]) => void
    }
) => {
    const { t } = useTranslation();

    const severityOptions = [
        { value: SeverityType.Critical, label: t('SeverityCritical') },
        { value: SeverityType.Warning, label: t('SeverityWarning') },
        { value: SeverityType.Minor, label: t('SeverityMinor') },
    ];

    const theme = useTheme();

    const dark = theme.theme==='dark';
    
    return (
        <Form id="alarmForm" className='form px-4'>
            <div className='row mb-2'>
                <div className="col-12 ">
                    <Field type="checkbox" name="active" />
                    <label className="ms-1" htmlFor="active">{t('Active')}</label>
                    <ErrorMessage name="active" component="div" className="danger" />
                </div>
            </div>
            <div className='row mb-2'>
                <div className="col-12 col-md-4">
                    <label>{t('Name')}</label>
                    <Field className="form-control" name="name" type="text" />
                    <ErrorMessage name="name" component="div" className="text-danger" />
                </div>
                <div className="col-12 col-md-8">
                    <label>{t('Description')}</label>
                    <Field className="form-control" name="description" type="text" />
                    <ErrorMessage name="description" component="div" className="text-danger" />
                </div>
            </div>
            <div className='row mb-2'>
                <div className="col-12 col-md-4">
                    <label>{t('Param')}</label>
                    <Field name="param" >
                        {({ field, form }: FieldProps) => (
                            <AWSelect dark={dark}
                                className='inputStyle selectStyle'
                                name={field.name}
                                options={[
                                    { value: "Param 1", label: "Parameter 1" },
                                    { value: "Param 2", label: "Parameter 2" },
                                    { value: "Param 3", label: "Parameter 3" },
                                    { value: "Param 4", label: "Parameter 4" },
                                    { value: "Param 5", label: "Parameter 5" },
                                    { value: "Param 6", label: "Parameter 6" },
                                    { value: "Param 7", label: "Parameter 7" },
                                    { value: "Param 8", label: "Parameter 8" },
                                    { value: "Param 9", label: "Parameter 9" }
                                ]}
                                value={field.value ? { value: field.value, label: field.value } : null}
                                onChange={(option) => form.setFieldValue("param", option?.value || "")}
                                clearable
                                placeholder={t("selectPlaceholder")}
                            />
                        )}
                    </Field>
                    <ErrorMessage name="param" component="div" className="text-danger" />
                </div>
                <div className="col-6 col-md-4">
                    <label>Min</label>
                    <Field className="form-control input" name="min" type="number" step="any" />
                    <ErrorMessage name="min" component="div" className="text-danger" />
                </div >
                <div className="col-6 col-md-4">
                    <label>Max</label>
                    <Field className="form-control input" name="max" type="number" step="any" />
                    <ErrorMessage name="max" component="div" className="text-danger" />
                </div>
            </div>
            <div className='row mb-2'>
                <div className="col-12 col-md-4">
                    <label>{t('SeverityType')}</label>
                    <Field name="severity">
                        {({ field, form }: FieldProps) => (
                            <AWSelect dark={dark}
                                className='inputStyle selectStyle'
                                name={field.name}
                                clearable
                                placeholder={t("selectPlaceholder")}
                                options={severityOptions}
                                value={severityOptions.find((option) => option.value === field.value) || null}
                                onChange={(option) => form.setFieldValue("severity", option?.value || "")}
                            />
                        )}
                    </Field>
                    <ErrorMessage name="severity" component="div" className="text-danger" />
                </div>
                <div className="col-8"></div>
            </div>
            <hr className="my-4 border-gray-300" />
            <div className='row mb-2'>
                <div className="col-12 col-md-4">
                    <label>{t('Installation')}</label>
                    <Field name="installation">
                        {({ field, form }: FieldProps) => (
                            <AWSelect  dark={dark}
                                className='inputStyle selectStyle'
                                name={field.name}
                                options={props.installations}
                                value={props.selectedInstallation}
                                onChange={(selectedOption) => {
                                    form.setFieldValue(field.name, selectedOption ? selectedOption.value : "")
                                    props.onSelectedInstallation(selectedOption!) // Llama también a tu función original
                                }}
                                onBlur={() => form.setFieldTouched(field.name, true)}
                                closeMenuOnSelect={true}
                                clearable={true}
                                searchable={true}
                                multi={false}
                                placeholder={t("selectPlaceholder")}
                            />
                        )}
                    </Field>
                    <ErrorMessage name="installation" component="div" className="text-danger" />
                </div>
                {props.selectedInstallation?.value != null && Number(props?.selectedInstallation?.value) >= 0 && ( // Convertim a number per no tenir problemes amb els types
                    <div className='col-12 col-md-8'>
                        <label>{t("Machines")}</label>
                        <Field name='machines'>
                            {({ field, form }: FieldProps) => (
                                <AWSelect  dark={dark}
                                    className='inputStyle selectStyle'
                                    name={field.name}
                                    options={props.machines}
                                    value={props.selectedMachines}
                                    onChange={(selectedOptions) => {
                                        const values = Array.isArray(selectedOptions)
                                            ? selectedOptions.map((opt) => opt.value)
                                            : []

                                        form.setFieldValue(field.name, values)
                                        props.onSelectedMachines(selectedOptions ? [...selectedOptions] : [])
                                    }}
                                    onBlur={() => form.setFieldTouched(field.name, true)}
                                    closeMenuOnSelect={true}
                                    clearable={true}
                                    searchable={true}
                                    multi={true}
                                    placeholder={t("selectPlaceholder_plural")}
                                />
                            )}
                        </Field>
                    <ErrorMessage name="machines" component="div" className="text-danger" />
                    </div>
                )}
            </div>
            <hr className="my-4 border-gray-300" />
            <div className='row mb-3'>
                <div className="col-12">
                    <label>{t('Emails')}</label>
                    <Field name="emails">
                        {({ field, form }: FieldProps) => (
                            <AWSelect  dark={dark}
                                creatable={true}
                                className='inputStyle selectStyle'
                                name={field.name}
                                multi
                                options={[]} // No necesitas opciones iniciales; puedes crearlas dinámicamente
                                value={(field.value || []).map((email: string) => ({
                                    value: email,
                                    label: email
                                }))}
                                onChange={(options: ISelectOptions[] | null) => {
                                    const emails = options ? options.map((opt: ISelectOptions) => opt.value) : []
                                    form.setFieldValue(field.name, emails)
                                }}
                                onBlur={() => form.setFieldTouched(field.name, true)}
                                placeholder={t("EmailPlaceholder")}
                                formatCreateLabel={(inputValue) => `${t("Add")}: "${inputValue}"`}
                            />
                        )}
                    </Field>
                    <ErrorMessage name="emails" component="div" className="error" />
                </div>
            </div>

            {/* {!props.loading && <div className='d-flex justify-content-end mt-2'>
                <button className='my-2 btn-Custom-Modal btn-Grey-Modal' onClick={props.onHide} type="button">{t('Cancel')}</button>
                <button className='my-2 ms-2 btn-Custom-Modal btn-Green-Modal' type="submit">{t('Save')}</button>
            </div>} */}
            {props.loading && (
                <div className='row mt-2'>
                    <div className='col'>
                        <div className='text-center mt-1'>
                            <Spinner animation='border' variant='success' />
                        </div>
                    </div>
                </div>
            )}
        </Form>
    )
}

export default ModalAlarm;